import React, {
  useCallback,
  useState,
} from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link,
} from '@mui/material'

import { Link as RouterLink } from 'react-router-dom'

import { api } from '@common-sense-privacy/common'

import usePolicyEvaluationStatus from '@/hooks/usePolicyEvaluationStatus'
import useLocation from '@/hooks/useLocation'
import { useCurrentOrganization } from '@/hooks/useSession'

import { useDismissEvaluationMutation } from '@/services/organization/product/evaluation'

import log from '@/utils/log'

import FailureDialog from './FailureDialog'

import { Props } from './types'

const {
  COMPLETE_EVALUATION_STATUS,
  ERROR_EVALUATION_STATUS,
} = api.endpoints.organization.product.evaluation.types

export const ANALYZING_TITLE = 'Analyzing Policy...'

export const ANALYZING_MESSAGE = 'We\'ll email you when it\'s ready for review.'

export const COMPLETE_MESSAGE = 'Policy Analysis Complete'

export const COMPLETE_LINK_LABEL = 'Start your review'

export const FAILED_MESSAGE = 'Policy Analysis Failed'

export const FAILED_LINK_LABEL = 'See what happened'

export const DISMISS_LABEL = 'Dismiss'

export const policyEvaluationPath = (productId: string): string => `/wizard/products/${productId}/policy-evaluation`

// Centre of the header's logo band: the 35px + 4px green account bar, then half of the
// 103px band itself. The card is pinned to that line rather than mounted inside
// `<Header />`, whose logo is centred against the full width and would be pushed off it.
const HEADER_LOGO_BAND_CENTER = 90

/**
 * From `lg` up the card sits in the empty right-hand half of the fixed header, level with
 * the logo, exactly as the design has it. Below `lg` the logo is too close to that space
 * to share it, so the card drops back into the flow above the wizard, full width.
 */
const cardSx = {
  alignItems: 'center',
  borderRadius: 1.5,
  mb: {
    lg: 0,
    xs: 3,
  },
  mx: {
    lg: 0,
    xs: 2,
  },
  position: { lg: 'fixed' },
  right: { lg: 24 },
  top: { lg: HEADER_LOGO_BAND_CENTER },
  transform: { lg: 'translateY(-50%)' },
  width: { lg: 'auto' },
  zIndex: { lg: 3 },
  '& .MuiAlert-icon': { py: 0 },
  // The close button sits on the same line as the title, not centred on the card.
  '& .MuiAlert-action': {
    alignItems: 'flex-start',
    pt: 0,
  },
}

// Lato has no 500, so MUI's medium-weight title would come out identical to the message
// underneath it. 700 is what keeps the two lines as distinct as the design has them.
const titleSx = {
  fontWeight: 700,
  lineHeight: '20px',
  mb: 0,
  mt: 0,
}

const messageSx = {
  color: 'inherit',
  fontSize: 14,
  lineHeight: '20px',
}

const linkSx = {
  ...messageSx,
  fontWeight: 400,
  textDecorationColor: 'inherit',
}

/**
 * Informational, never an error state: the automated analysis runs alongside the wizard
 * and the user is expected to keep answering questions while it does.
 *
 * The terminal states carry an X. Dismissal is stored on the evaluation row, so it holds
 * across reloads and devices and a retry's fresh row starts out visible again; the only
 * other way it closes is the backend dismissing a finished card after a week. The
 * analyzing state has no X on purpose - it replaces itself within minutes, and closing it
 * would hide the completed results link before the user ever saw it.
 */
function PolicyEvaluationCard({ productId }: Props): React.ReactElement | null {
  const {
    evaluation,
    isLoading,
  } = usePolicyEvaluationStatus(productId)
  const { pathname } = useLocation()
  const { id: organizationId } = useCurrentOrganization()

  const [ dismissEvaluation ] = useDismissEvaluationMutation()

  const [
    isFailureDialogOpen,
    setIsFailureDialogOpen,
  ] = useState(false)

  const closeFailureDialog = useCallback(() => setIsFailureDialogOpen(false), [])

  // Closing is all this has to do: the retry's `invalidatesTags` refetches the latest
  // evaluation, and the fresh non-terminal row is what puts the card back into its
  // analyzing state and restarts `usePolicyEvaluationStatus`'s interval.
  const handleRetried = closeFailureDialog

  // The mutation patches the `latest` cache on the spot, which is what closes the card;
  // a rejected request undoes that patch, so there is nothing to show the user here
  // beyond the card coming back.
  const handleDismiss = useCallback(() => {
    if (!evaluation || !productId) return

    dismissEvaluation({
      params: {
        id: organizationId,
        productEvaluationId: evaluation.id,
        productId,
      },
    })
      .unwrap()
      .catch(error => log('error', 'Failed to dismiss the policy evaluation card.', error))
  }, [
    dismissEvaluation,
    evaluation,
    organizationId,
    productId,
  ])

  // Nothing to say yet - either this product has never been evaluated (the endpoint
  // answers null rather than 404) or the first poll is still in flight. Both render
  // nothing at all, so the wizard never shifts under a skeleton that flashes once.
  if (isLoading || !evaluation || !productId) {
    // eslint-disable-next-line no-null/no-null
    return null
  }

  const evaluationPath = policyEvaluationPath(productId)

  // The card exists to point at the results screen; on that screen it is just noise.
  if (pathname === evaluationPath || pathname.startsWith(`${evaluationPath}/`)) {
    // eslint-disable-next-line no-null/no-null
    return null
  }

  // Closed - by the X, or by the backend a week after the evaluation finished.
  if (evaluation.dismissedAt) {
    // eslint-disable-next-line no-null/no-null
    return null
  }

  if (evaluation.status === COMPLETE_EVALUATION_STATUS) {
    // A completed evaluation that mapped no answers has nothing for the user to review,
    // so it gets no card at all rather than a link to an empty screen.
    if (!evaluation.answerCount) {
      // eslint-disable-next-line no-null/no-null
      return null
    }

    return (
      <Alert
        data-testid='policy-evaluation-card'
        severity='success'
        sx={cardSx}
        onClose={handleDismiss}
        closeText={DISMISS_LABEL}
      >
        <AlertTitle sx={titleSx}>{COMPLETE_MESSAGE}</AlertTitle>
        <Box sx={messageSx}>
          <Link component={RouterLink} to={evaluationPath} color='inherit' sx={linkSx}>{COMPLETE_LINK_LABEL}</Link>.
        </Box>
      </Alert>
    )
  }

  if (evaluation.status === ERROR_EVALUATION_STATUS) {
    return (
      <>
        <Alert
          data-testid='policy-evaluation-card'
          severity='warning'
          sx={cardSx}
          onClose={handleDismiss}
          closeText={DISMISS_LABEL}
        >
          <AlertTitle sx={titleSx}>{FAILED_MESSAGE}</AlertTitle>
          <Box sx={messageSx}>
            <Button
              variant='link'
              onClick={() => setIsFailureDialogOpen(true)}
              sx={{
                ...linkSx,
                color: 'inherit',
                textDecoration: 'underline',
                verticalAlign: 'baseline',
              }}
            >
              {FAILED_LINK_LABEL}
            </Button>.
          </Box>
        </Alert>
        <FailureDialog
          open={isFailureDialogOpen}
          onClose={closeFailureDialog}
          onRetried={handleRetried}
          productId={productId}
        />
      </>
    )
  }

  // Everything the contract calls terminal has its own branch above, so what is left is
  // in progress - including a status neither side recognises, which stays analyzing for
  // exactly as long as `isTerminalEvaluationStatus` keeps the hook polling it.
  return (
    <Alert
      data-testid='policy-evaluation-card'
      severity='info'
      sx={cardSx}
    >
      <AlertTitle sx={titleSx}>{ANALYZING_TITLE}</AlertTitle>
      <Box sx={messageSx}>{ANALYZING_MESSAGE}</Box>
    </Alert>
  )
}

export default React.memo(PolicyEvaluationCard)
